<template>
  <q-page class="ot-page">
    <div class="ot-container">
      <!-- Loading state -->
      <template v-if="loading">
        <div style="display: flex; align-items: flex-end; justify-content: space-between;">
          <div>
            <q-skeleton type="text" width="200px" dark style="font-size: 1.75rem;" />
            <q-skeleton type="text" width="280px" dark style="margin-top: 4px;" />
          </div>
          <q-skeleton type="rect" width="180px" height="40px" dark style="border-radius: 8px;" />
        </div>
        <div class="kpi-grid" style="margin-top: 24px;">
          <q-skeleton v-for="n in 8" :key="n" type="rect" height="80px" dark style="border-radius: 12px;" />
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 24px;">
          <q-skeleton type="rect" height="220px" dark style="border-radius: 12px;" />
          <q-skeleton type="rect" height="220px" dark style="border-radius: 12px;" />
        </div>
        <q-skeleton type="rect" height="200px" dark style="border-radius: 12px; margin-top: 24px;" />
      </template>

      <!-- Error state -->
      <div v-else-if="error" class="ot-empty">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p class="empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchAllData"
        />
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="ot-header">
          <div>
            <h1 class="ot-title">Dashboard Operativo</h1>
            <p class="ot-subtitle">Metricas, KPIs y estado de sedes</p>
          </div>
          <q-select
            v-if="sedes.length > 0"
            v-model="selectedSedeId"
            :options="sedeOptions"
            emit-value
            map-options
            dense
            outlined
            dark
            style="min-width: 200px;"
            :style="{
              background: 'var(--oa-bg-2)',
              borderRadius: '8px',
            }"
            @update:model-value="onSedeChange"
          />
        </div>

        <!-- KPI Cards -->
        <div class="kpi-grid">
          <div v-for="kpi in kpiCards" :key="kpi.label" class="kpi-card">
            <div class="kpi-icon-wrap">
              <q-icon :name="kpi.icon" size="20px" style="color: var(--oa-gold-full);" />
            </div>
            <div>
              <p class="kpi-value">{{ kpi.value }}</p>
              <p class="kpi-label">{{ kpi.label }}</p>
            </div>
          </div>
        </div>

        <!-- OT Level + Gates -->
        <div class="ot-two-col">
          <!-- OT Level -->
          <div class="ot-card">
            <h3 class="section-title">Nivel OT</h3>
            <template v-if="otLevel">
              <div class="level-header">
                <div>
                  <p class="level-value">{{ otLevel.level }}</p>
                  <p class="level-sub">{{ otLevel.aap }} AAP actuales</p>
                </div>
                <div style="text-align: right;">
                  <p class="level-next-label">Proximo gate</p>
                  <p class="level-next-value">{{ otLevel.nextGate }} AAP</p>
                </div>
              </div>
              <q-linear-progress
                :value="(otLevel.progress ?? 0) / 100"
                color="amber"
                track-color="grey-9"
                rounded
                size="8px"
                style="margin-top: 12px;"
              />
              <div class="level-gates-grid">
                <div
                  v-for="lvl in otLevelSteps"
                  :key="lvl.label"
                  class="level-gate-item"
                  :style="{
                    background: otLevel.aap >= lvl.gate
                      ? 'color-mix(in srgb, var(--oa-tier-ot) 12%, transparent)'
                      : 'var(--oa-bg-3)',
                    color: otLevel.aap >= lvl.gate ? 'var(--oa-tier-ot)' : 'var(--oa-text-disabled)',
                    border: otLevel.aap >= lvl.gate
                      ? '1px solid color-mix(in srgb, var(--oa-tier-ot) 25%, transparent)'
                      : '1px solid var(--oa-border)',
                  }"
                >
                  <p style="font-weight: 700;">{{ lvl.label }}</p>
                  <p>{{ lvl.gate }} AAP</p>
                </div>
              </div>
            </template>
            <p v-else class="no-data-text">Sin datos de nivel</p>
          </div>

          <!-- Gate Evaluations -->
          <div class="ot-card">
            <h3 class="section-title">Gates de Progresion</h3>
            <template v-if="gates && gates.length > 0">
              <div class="gates-list">
                <div
                  v-for="g in gates"
                  :key="g.id"
                  class="gate-item"
                  :class="'gate-' + g.status"
                >
                  <q-icon :name="gateIcons[g.status] || 'schedule'" size="16px" :class="'gate-icon-' + g.status" />
                  <div class="gate-info">
                    <p class="gate-name">{{ gateLabels[g.gateType] || g.gateType }}</p>
                    <p class="gate-values">Target: {{ g.targetValue }} | Actual: {{ g.actualValue }}</p>
                  </div>
                  <q-badge
                    :label="gateStatusLabel(g.status)"
                    :style="{
                      background: gateStatusBg(g.status),
                      color: gateStatusColor(g.status),
                      fontSize: '11px',
                      padding: '2px 10px',
                      borderRadius: '6px',
                    }"
                  />
                </div>
              </div>
            </template>
            <p v-else class="no-data-text">Sin evaluaciones de gate</p>
          </div>
        </div>

        <!-- AAP History Chart -->
        <div class="ot-card">
          <h3 class="section-title">Evolucion AAP (30 dias)</h3>
          <template v-if="aapHistory && aapHistory.length > 0">
            <div class="aap-chart-wrap">
              <canvas ref="aapChartCanvas" />
            </div>
          </template>
          <p v-else class="no-data-text">Sin datos AAP</p>
        </div>

        <!-- AURA per Sede + Exam Stats -->
        <div class="ot-two-col">
          <!-- AURA per Sede -->
          <div class="ot-card">
            <h3 class="section-title">AURA por Sede</h3>
            <template v-if="auraPerSede && auraPerSede.length > 0">
              <div class="sede-stat-list">
                <div v-for="s in auraPerSede" :key="s.sedeId" class="sede-stat-row">
                  <div class="sede-stat-left">
                    <div class="sede-code-badge">{{ s.sedeCode }}</div>
                    <div>
                      <p class="sede-stat-name">{{ s.sedeName }}</p>
                      <p class="sede-stat-sub">{{ s.userCount }} usuarios activos</p>
                    </div>
                  </div>
                  <div class="sede-stat-right">
                    <p class="sede-stat-value">{{ Number(s.totalAura).toLocaleString() }}</p>
                    <p class="sede-stat-label">AURA total</p>
                  </div>
                </div>
              </div>
            </template>
            <p v-else class="no-data-text">Sin datos de AURA</p>
          </div>

          <!-- Exam Stats per Sede -->
          <div class="ot-card">
            <h3 class="section-title">Examenes por Sede</h3>
            <template v-if="examStats && examStats.length > 0">
              <div class="sede-stat-list">
                <div v-for="s in examStats" :key="s.sedeId" class="sede-stat-row">
                  <div class="sede-stat-left">
                    <div class="sede-code-badge">{{ s.sedeCode }}</div>
                    <div>
                      <p class="sede-stat-name">{{ s.sedeName }}</p>
                      <p class="sede-stat-sub">{{ s.passedExams }}/{{ s.totalExams }} aprobados</p>
                    </div>
                  </div>
                  <q-badge
                    :label="s.passRate + '%'"
                    :style="{
                      background: s.passRate >= 80
                        ? 'color-mix(in srgb, var(--oa-gold-full) 15%, transparent)'
                        : 'var(--oa-bg-3)',
                      color: s.passRate >= 80 ? 'var(--oa-gold-full)' : 'var(--oa-text-secondary)',
                      fontSize: '12px',
                      padding: '4px 10px',
                      borderRadius: '6px',
                    }"
                  />
                </div>
              </div>
            </template>
            <p v-else class="no-data-text">Sin datos de examenes</p>
          </div>
        </div>

        <!-- Variables A/B/C + Monthly Metrics -->
        <div class="ot-two-col">
          <!-- Variables -->
          <div class="ot-card">
            <h3 class="section-title">
              Variables Economicas
              <span v-if="variables" class="section-title-meta">
                {{ monthNames[variables.month] }} {{ variables.year }}
                <template v-if="!variables.isFinalized"> (provisional)</template>
              </span>
            </h3>
            <template v-if="variables">
              <div class="var-revenue-grid">
                <div class="var-box var-box-income">
                  <p class="var-box-label" style="color: var(--oa-success);">Ingresos</p>
                  <p class="var-box-value" style="color: var(--oa-success);">{{ formatCurrency(variables.revenue) }}</p>
                </div>
                <div class="var-box var-box-expense">
                  <p class="var-box-label" style="color: var(--oa-danger);">Gastos</p>
                  <p class="var-box-value" style="color: var(--oa-danger);">{{ formatCurrency(variables.expenses) }}</p>
                </div>
              </div>
              <div class="var-abc-grid">
                <div class="var-abc-item">
                  <p class="var-abc-label">Variable A</p>
                  <p class="var-abc-value">{{ formatCurrency(variables.variableA) }}</p>
                  <p class="var-abc-desc">Profit Share</p>
                </div>
                <div class="var-abc-item">
                  <p class="var-abc-label">Variable B</p>
                  <p class="var-abc-value">{{ formatCurrency(variables.variableB) }}</p>
                  <p class="var-abc-desc">Bono Crecimiento</p>
                </div>
                <div class="var-abc-item">
                  <p class="var-abc-label">Variable C</p>
                  <p class="var-abc-value">{{ formatCurrency(variables.variableC) }}</p>
                  <p class="var-abc-desc">Comision</p>
                </div>
              </div>
              <div class="var-footer-grid">
                <div class="var-footer-item">
                  <p class="var-footer-label">RNPI</p>
                  <p class="var-footer-value">{{ formatCurrency(variables.rnpi) }}</p>
                </div>
                <div class="var-footer-item">
                  <p class="var-footer-label">Holdback 80%</p>
                  <p class="var-footer-value">{{ formatCurrency(variables.holdbackMonthly) }}</p>
                </div>
                <div class="var-footer-item">
                  <p class="var-footer-label">Holdback 20%</p>
                  <p class="var-footer-value">{{ formatCurrency(variables.holdbackQuarterly) }}</p>
                </div>
              </div>
            </template>
            <p v-else class="no-data-text">Sin datos de este mes</p>
          </div>

          <!-- Monthly Metrics Table -->
          <div class="ot-card">
            <h3 class="section-title">Metricas Mensuales 2026</h3>
            <template v-if="metrics && metrics.length > 0">
              <q-table
                :rows="metrics"
                :columns="metricsColumns"
                row-key="id"
                flat
                dark
                dense
                hide-pagination
                :pagination="{ rowsPerPage: 0 }"
                class="metrics-table"
              />
            </template>
            <p v-else class="no-data-text">Sin metricas mensuales</p>
          </div>
        </div>

        <!-- Sedes Grid -->
        <div class="ot-card">
          <h3 class="section-title">Sedes</h3>
          <div class="sedes-grid">
            <div
              v-for="sede in sedes"
              :key="sede.id"
              class="sede-card"
              :class="{ 'sede-card-active': sede.id === selectedSedeId }"
              @click="onSedeClick(sede.id)"
            >
              <div class="sede-card-header">
                <div>
                  <p class="sede-card-name">{{ sede.name }}</p>
                  <p class="sede-card-location">{{ sede.city }}, {{ sede.province }} | {{ sede.code }}</p>
                </div>
                <div class="sede-card-right">
                  <span class="sede-card-aap">{{ sede.currentAap }} AAP</span>
                  <q-badge
                    :label="sede.status === 'active' ? 'Activa' : sede.status === 'pilot' ? 'Piloto' : sede.status"
                    :style="{
                      background: sede.status === 'active'
                        ? 'color-mix(in srgb, var(--oa-gold-full) 15%, transparent)'
                        : 'var(--oa-bg-3)',
                      color: sede.status === 'active' ? 'var(--oa-gold-full)' : 'var(--oa-text-secondary)',
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '6px',
                    }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick, onBeforeUnmount } from 'vue';
import { api } from 'boot/axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ---------- Types ----------
interface Sede {
  id: string;
  name: string;
  code: string;
  city: string;
  province: string;
  status: string;
  currentAap: number;
  maxCapacity?: number;
}

interface KpiData {
  totalAap: number;
  totalUsers: number;
  activeEnrollments: number;
  completedEnrollments: number;
  retentionRate: number;
  activeSedes: number;
  totalSedes: number;
  activeIncorporations: number;
}

interface AapSnapshot {
  snapshotDate: string;
  aapCount: number;
}

interface GateEvaluation {
  id: string;
  gateType: string;
  status: string;
  targetValue: string;
  actualValue: string;
}

interface OtLevel {
  aap: number;
  level: string;
  nextGate: number;
  progress: number;
}

interface Variables {
  month: number;
  year: number;
  revenue: string | null;
  expenses: string | null;
  rai: string | null;
  rnpi: string | null;
  variableA: string | null;
  variableB: string | null;
  variableC: string | null;
  holdbackMonthly: string | null;
  holdbackQuarterly: string | null;
  isFinalized?: boolean;
}

interface MetricRow {
  id: string;
  month: number;
  aapStart: number;
  aapEnd: number;
  enrollments: number;
  churned: number;
  winbacks: number;
  churnRate: string;
  revenue: string | null;
  expenses: string | null;
  rai: string | null;
  isFinalized: boolean;
}

interface AuraSede {
  sedeId: string;
  sedeName: string;
  sedeCode: string;
  totalAura: number;
  userCount: number;
}

interface ExamSede {
  sedeId: string;
  sedeName: string;
  sedeCode: string;
  totalExams: number;
  passedExams: number;
  passRate: number;
}

// ---------- State ----------
const loading = ref(true);
const error = ref<string | null>(null);

const kpis = ref<KpiData | null>(null);
const sedes = ref<Sede[]>([]);
const selectedSedeId = ref<string>('');
const aapHistory = ref<AapSnapshot[]>([]);
const gates = ref<GateEvaluation[]>([]);
const otLevel = ref<OtLevel | null>(null);
const variables = ref<Variables | null>(null);
const metrics = ref<MetricRow[]>([]);
const auraPerSede = ref<AuraSede[]>([]);
const examStats = ref<ExamSede[]>([]);

const aapChartCanvas = ref<HTMLCanvasElement | null>(null);
let aapChartInstance: ChartJS | null = null;

// ---------- Constants ----------
const gateLabels: Record<string, string> = {
  churn_retention: 'Retencion / Churn',
  audit_standard: 'Standard de Auditoria',
  space_usability: 'Usabilidad del Espacio',
  minimum_cash: 'Caja Minima',
  exclusive_ops: 'Operacion Exclusiva',
};

const gateIcons: Record<string, string> = {
  passed: 'check_circle',
  failed: 'cancel',
  pending: 'schedule',
  waived: 'remove',
};

const monthNames = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const otLevelSteps = [
  { label: 'OT-1', gate: 150 },
  { label: 'OT-2', gate: 200 },
  { label: 'OT-3', gate: 250 },
];

// ---------- Computed ----------
const sedeOptions = computed(() =>
  sedes.value.map((s) => ({ label: `${s.name} (${s.code})`, value: s.id })),
);

const kpiCards = computed(() => {
  if (!kpis.value) return [];
  const k = kpis.value;
  return [
    { label: 'AAP Total', value: k.totalAap, icon: 'people' },
    { label: 'Usuarios Activos', value: k.totalUsers, icon: 'person_add' },
    { label: 'Inscripciones Activas', value: k.activeEnrollments, icon: 'trending_up' },
    { label: 'Retencion', value: k.retentionRate + '%', icon: 'percent' },
    { label: 'Sedes Activas', value: k.activeSedes, icon: 'location_on' },
    { label: 'Sedes Totales', value: k.totalSedes, icon: 'location_on' },
    { label: 'Incorporaciones', value: k.activeIncorporations, icon: 'person_add' },
    { label: 'Cursos Completados', value: k.completedEnrollments, icon: 'bar_chart' },
  ];
});

const metricsColumns = [
  { name: 'month', label: 'Mes', field: (row: MetricRow) => monthNames[row.month], align: 'left' as const },
  { name: 'aapEnd', label: 'AAP', field: 'aapEnd', align: 'right' as const },
  { name: 'enrollments', label: '+Altas', field: 'enrollments', align: 'right' as const,
    format: (val: number) => '+' + val },
  { name: 'churned', label: '-Bajas', field: 'churned', align: 'right' as const,
    format: (val: number) => '-' + val },
  { name: 'churnRate', label: 'Churn', field: 'churnRate', align: 'right' as const,
    format: (val: string) => val + '%' },
  { name: 'status', label: 'Estado', field: 'isFinalized', align: 'right' as const,
    format: (val: boolean) => val ? 'Final' : 'Prov.' },
];

// ---------- Helpers ----------
function formatCurrency(value: string | null | undefined): string {
  if (!value) return '$0';
  const num = parseFloat(value);
  if (isNaN(num)) return '$0';
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
}

function gateStatusLabel(status: string): string {
  switch (status) {
    case 'passed': return 'OK';
    case 'failed': return 'Fallo';
    case 'pending': return 'Pendiente';
    case 'waived': return 'Excep.';
    default: return status;
  }
}

function gateStatusBg(status: string): string {
  switch (status) {
    case 'passed': return 'color-mix(in srgb, var(--oa-success) 15%, transparent)';
    case 'failed': return 'color-mix(in srgb, var(--oa-danger) 15%, transparent)';
    case 'pending': return 'color-mix(in srgb, var(--oa-gold-full) 15%, transparent)';
    default: return 'var(--oa-bg-3)';
  }
}

function gateStatusColor(status: string): string {
  switch (status) {
    case 'passed': return 'var(--oa-success)';
    case 'failed': return 'var(--oa-danger)';
    case 'pending': return 'var(--oa-gold-full)';
    default: return 'var(--oa-text-secondary)';
  }
}

// ---------- Chart ----------
function renderAapChart() {
  if (!aapChartCanvas.value || !aapHistory.value.length) return;

  if (aapChartInstance) {
    aapChartInstance.destroy();
    aapChartInstance = null;
  }

  const labels = aapHistory.value.map((s) => {
    const d = new Date(s.snapshotDate);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  });
  const data = aapHistory.value.map((s) => s.aapCount);

  aapChartInstance = new ChartJS(aapChartCanvas.value, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'AAP',
          data,
          borderColor: 'var(--color-terracotta)',
          borderWidth: 2,
          backgroundColor: 'rgba(232, 184, 75, 0.1)',
          pointBackgroundColor: 'var(--color-terracotta)',
          pointBorderColor: 'var(--color-terracotta)',
          pointRadius: 3,
          pointHoverRadius: 5,
          fill: true,
          tension: 0.3,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'var(--color-sandy-beige)',
          titleColor: 'var(--color-deep-charcoal)',
          bodyColor: 'var(--color-charcoal-mist)',
          borderColor: 'rgba(61, 55, 50, 0.12)',
          borderWidth: 1,
        },
      },
      scales: {
        x: {
          ticks: { color: 'rgba(160, 152, 144, 0.5)', font: { size: 10 } },
          grid: { color: 'rgba(61, 58, 54, 0.15)' },
        },
        y: {
          ticks: { color: 'rgba(160, 152, 144, 0.5)', font: { size: 10 } },
          grid: { color: 'rgba(61, 58, 54, 0.15)' },
        },
      },
    },
  });
}

// ---------- Data Fetching ----------
async function fetchDashboard() {
  const { data: res } = await api.get('/ot');
  kpis.value = res.kpis;
  sedes.value = res.sedes || [];
  if (sedes.value.length > 0 && !selectedSedeId.value) {
    selectedSedeId.value = sedes.value[0].id;
  }
}

async function fetchSedeData() {
  if (!selectedSedeId.value) return;
  const sid = selectedSedeId.value;

  const [aapRes, metricsRes, gatesRes, levelRes, varsRes] = await Promise.all([
    api.get(`/ot/aap-history/${sid}`, { params: { days: 30 } }).catch(() => ({ data: [] })),
    api.get(`/ot/metrics/${sid}`, { params: { year: 2026 } }).catch(() => ({ data: [] })),
    api.get(`/ot/gates/${sid}`).catch(() => ({ data: [] })),
    api.get(`/ot/level/${sid}`).catch(() => ({ data: null })),
    api.get(`/ot/variables/${sid}`).catch(() => ({ data: null })),
  ]);

  aapHistory.value = aapRes.data;
  metrics.value = metricsRes.data;
  gates.value = gatesRes.data;
  otLevel.value = levelRes.data;
  variables.value = varsRes.data;

  await nextTick();
  renderAapChart();
}

async function fetchGlobalData() {
  const [auraRes, examRes] = await Promise.all([
    api.get('/ot/aura-per-sede').catch(() => ({ data: [] })),
    api.get('/ot/exam-stats').catch(() => ({ data: [] })),
  ]);
  auraPerSede.value = auraRes.data;
  examStats.value = examRes.data;
}

async function fetchAllData() {
  loading.value = true;
  error.value = null;
  try {
    await fetchDashboard();
    await Promise.all([fetchSedeData(), fetchGlobalData()]);
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Error al cargar el dashboard';
    error.value = msg;
  } finally {
    loading.value = false;
  }
}

function onSedeChange() {
  fetchSedeData();
}

function onSedeClick(sedeId: string) {
  selectedSedeId.value = sedeId;
  fetchSedeData();
}

// ---------- Lifecycle ----------
onMounted(() => {
  fetchAllData();
});

onBeforeUnmount(() => {
  if (aapChartInstance) {
    aapChartInstance.destroy();
    aapChartInstance = null;
  }
});
</script>

<style scoped>
.ot-page {
  padding: 24px;
}

.ot-container {
  max-width: 1200px;
  margin: 0 auto;
}

.ot-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 12px;
}

.empty-text {
  color: var(--oa-text-muted);
  font-size: 14px;
}

/* Header */
.ot-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 24px;
}

.ot-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.ot-subtitle {
  font-size: 0.875rem;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

@media (max-width: 768px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
}

.kpi-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--oa-gold-full) 12%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kpi-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
  line-height: 1.2;
}

.kpi-label {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

/* Two column layout */
.ot-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 768px) {
  .ot-two-col {
    grid-template-columns: 1fr;
  }
}

/* Cards */
.ot-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.ot-two-col .ot-card {
  margin-bottom: 0;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 16px;
}

.section-title-meta {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
  font-weight: 400;
  margin-left: 8px;
}

.no-data-text {
  color: var(--oa-text-muted);
  font-size: 0.875rem;
}

/* OT Level */
.level-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.level-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-gold-full);
  margin: 0;
}

.level-sub {
  font-size: 0.875rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

.level-next-label {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
  margin: 0;
}

.level-next-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 2px 0 0;
}

.level-gates-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 16px;
}

.level-gate-item {
  padding: 8px;
  border-radius: 8px;
  text-align: center;
  font-size: 0.75rem;
}

/* Gates */
.gates-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gate-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid var(--oa-border);
}

.gate-passed {
  border-color: var(--oa-success);
}

.gate-failed {
  border-color: var(--oa-danger);
}

.gate-pending {
  border-color: var(--oa-gold-dim);
}

.gate-icon-passed {
  color: var(--oa-success);
}

.gate-icon-failed {
  color: var(--oa-danger);
}

.gate-icon-pending {
  color: var(--oa-gold-full);
}

.gate-icon-waived {
  color: var(--oa-text-muted);
}

.gate-info {
  flex: 1;
  min-width: 0;
}

.gate-name {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gate-values {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

/* AAP Chart */
.aap-chart-wrap {
  height: 200px;
  position: relative;
}

/* Sede stat list (AURA / Exams) */
.sede-stat-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sede-stat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sede-stat-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sede-code-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--oa-gold-full);
}

.sede-stat-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.sede-stat-sub {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

.sede-stat-right {
  text-align: right;
}

.sede-stat-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--oa-gold-full);
  margin: 0;
}

.sede-stat-label {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

/* Variables */
.var-revenue-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.var-box {
  padding: 12px;
  border-radius: 12px;
}

.var-box-income {
  background: color-mix(in srgb, var(--oa-success) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--oa-success) 15%, transparent);
}

.var-box-expense {
  background: color-mix(in srgb, var(--oa-danger) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--oa-danger) 15%, transparent);
}

.var-box-label {
  font-size: 0.6875rem;
  margin: 0;
}

.var-box-value {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 4px 0 0;
}

.var-abc-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.var-abc-item {
  text-align: center;
  padding: 8px;
  background: color-mix(in srgb, var(--oa-gold-full) 5%, transparent);
  border-radius: 8px;
}

.var-abc-label {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  margin: 0;
}

.var-abc-value {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--oa-gold-full);
  margin: 4px 0;
}

.var-abc-desc {
  font-size: 0.625rem;
  color: var(--oa-text-muted);
  margin: 0;
}

.var-footer-grid {
  display: flex;
  gap: 8px;
}

.var-footer-item {
  flex: 1;
  text-align: center;
  padding: 8px;
  background: var(--oa-bg-3);
  border-radius: 8px;
}

.var-footer-label {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  margin: 0;
}

.var-footer-value {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 4px 0 0;
}

/* Metrics table */
.metrics-table {
  background: transparent !important;
}

.metrics-table :deep(.q-table__top),
.metrics-table :deep(.q-table__bottom) {
  display: none;
}

.metrics-table :deep(th) {
  color: var(--oa-text-muted) !important;
  font-size: 0.75rem !important;
  font-weight: 500 !important;
  border-bottom: 1px solid var(--oa-border) !important;
}

.metrics-table :deep(td) {
  color: var(--oa-text-primary) !important;
  font-size: 0.8125rem !important;
  border-bottom: 1px solid color-mix(in srgb, var(--oa-border) 30%, transparent) !important;
}

/* Sedes grid */
.sedes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (max-width: 768px) {
  .sedes-grid {
    grid-template-columns: 1fr;
  }
}

.sede-card {
  padding: 16px;
  background: var(--oa-bg-3);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.sede-card:hover {
  border-color: color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
}

.sede-card-active {
  border-color: var(--oa-gold-full);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
}

.sede-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sede-card-name {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.sede-card-location {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

.sede-card-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sede-card-aap {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--oa-gold-full);
}

/* Skeleton grid */
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
</style>
